import { checkoutTotals, moneyToCents } from "./checkout-totals.mjs";

// Wallets whose native sheet must open inside the click's user activation.
// Safari refuses to create an ApplePaySession after awaited network calls
// ("Unable to show Apple Pay"), so for these elements.submit() runs first.
export const WALLET_PAYMENT_TYPES = Object.freeze(["apple_pay", "google_pay"]);

export const isWalletPayment = (type) =>
  WALLET_PAYMENT_TYPES.includes(String(type || ""));

export const PAYMENT_FALLBACK_MESSAGE =
  "We couldn't complete your payment. You have not been charged. Please try again or contact us if it keeps happening.";

// Server and SDK texts reach the shopper only when they read as sentences for
// people. Transport errors, stack-ish texts and internal names do not.
// Apollo 2 prefixes transport failures with "Network error: " (with a space)
// and the HTTP link adds "Response not successful: Received status code N";
// Safari says "Load failed"; a proxy's HTML page breaks JSON parsing; PHP and
// MySQL warnings can leak into a response body.
const TECHNICAL = new RegExp(
  [
    "internal server error|graphql|exception|undefined|null\\b|\\bfailed:",
    "missing \\w*(id|secret|url)\\b|clientsecret|paymentintent|initpayment|confirmorder",
    "network\\s*error|network request failed|failed to fetch|fetch failed|load failed",
    "response not successful|status code|unexpected (token|end)|\\bjson\\b",
    "syntaxerror|typeerror|referenceerror|cannot read propert|is not (a function|defined)",
    "failed to execute|localstorage|sessionstorage|quota",
    "sqlstate|\\b(warning|notice|fatal error|parse error):|stack trace|\\.php\\b|on line \\d+",
  ].join("|"),
  "i"
);

export const customerPaymentMessage = (
  message,
  fallback = PAYMENT_FALLBACK_MESSAGE
) => {
  const text = String(message ?? "").trim();
  if (!text || text.length > 300 || TECHNICAL.test(text)) return fallback;
  return text;
};

// Stripe reliability 1.0.0-rc5 sends these exact texts as the createOrder,
// initPayment or confirmOrder GraphQL error when the shopper can act on the
// cause (HooligansStripePolicy::MESSAGE_*, shared/opencart-tools/
// stripe-reliability/README.md). Apollo prefixes them with "GraphQL error: ",
// which customerPaymentMessage rejects, so they are matched here instead; any
// other server text keeps the checkout's own fixed message.
export const PAYMENT_ATTEMPT_REPLACED_MESSAGE =
  "Your order details changed, so we refreshed the secure payment form. Please enter your payment details again and confirm. You have not been charged.";
export const SERVER_PAYMENT_MESSAGES = Object.freeze([
  "We couldn't use this email address for payment. Please check it and try again.",
  "Please check your billing and delivery address: street, city, state and ZIP code are required.",
  "Please check your name and email address, then try again.",
  "A payment for this checkout is already in progress. Please refresh the page to see its status before trying again.",
  "Your payment is still being prepared. Please wait a few seconds and try again.",
  "Your order total needs to be updated. Please refresh the page and try again. You have not been charged.",
  PAYMENT_ATTEMPT_REPLACED_MESSAGE,
  "This order total can't be paid online. Please contact us and we'll complete your order.",
  "This payment was canceled. Please refresh the page to start a new payment.",
]);

const graphQLErrors = (error) => [
  ...(Array.isArray(error?.graphQLErrors) ? error.graphQLErrors : []),
  ...(Array.isArray(error?.networkError?.result?.errors)
    ? error.networkError.result.errors
    : []),
];

// The known shopper text carried by this failed request, else null.
export const serverPaymentMessage = (error) => {
  for (const entry of graphQLErrors(error)) {
    const text = String(entry?.message ?? "");
    if (SERVER_PAYMENT_MESSAGES.includes(text)) return text;
  }
  return null;
};

// The amount the shopper saw at the click must equal what the server priced
// right before payment; otherwise the attempt stops before any order or charge.
export const totalChanged = (shownCents, serverCents) =>
  !Number.isInteger(shownCents) ||
  !Number.isInteger(serverCents) ||
  shownCents !== serverCents;

export const orderTotalText = (rows) =>
  String(checkoutTotals(rows).total?.text || "");

export const orderTotalCents = (rows) => moneyToCents(orderTotalText(rows));
